import { AnswerStep, AnswerTypeId } from '../../../../dorian-shared/types/branch/AnswerStep';
import { Branch } from '../../../../dorian-shared/types/branch/Branch';
import { BranchStep, BranchStepMemoryAction, BranchStepMemoryRoll } from '../../../../dorian-shared/types/branch/BranchStep';
import { Character } from '../../../../dorian-shared/types/character/Character';
import { ExpressionConfig } from '../../../../dorian-shared/types/character/ExpressionConfig';
import { EpisodeTextImport } from '../../../../services/EpisodeTextService/models/EpisodeTextImport';
import { EpisodeTextImportNode } from '../../../../services/EpisodeTextService/models/EpisodeTextImportNode';
import { EpisodeTextImportStep } from '../../../../services/EpisodeTextService/models/EpisodeTextImportStep';
import { EpisodeTextImportHelper } from '../../../../services/EpisodeTextService/models/EpisodeTextImportHelper';
import { EpisodeTextImportCommandKey } from '../../../../services/EpisodeTextService/types/episodeTextImportCommandTypes';
import { EpisodeTextEntityType } from '../../../../services/EpisodeTextService/types/episodeTextImportTypes';
import { resolveExpressionId } from '../../../../services/EpisodeTextService/utils/resolveExpressionId';
import {
  CheckOperator,
  isCharacterReferenceMemoryType,
  MemoryCheckValueType,
  MemoryDTO,
  MemorySingleLink,
  MemoryType,
  StepCheck,
  StepCheckOperatorType,
  StepCheckSwitch,
} from '../../../../components/pages/Book/MemoryBank/memoryBankTypes';
import { parseReqCommand } from '../../../../services/EpisodeTextService/models/parseReqCommand';

// ---------------------------------------------------------------------------
// Types matching PATCH /v1/stories/:story_id/edit-context
// ---------------------------------------------------------------------------

type AnswerRequirement = {
  check: StepCheck;
  visualTreatmentIfNotMet: string;
  isBadgeHidden: boolean;
};

type PayloadAnswer = {
  id: number;
  text?: string;
  gotoBranchId?: number | null;
  answerTypeId?: number;
  answerPrice?: number;
  requirement?: AnswerRequirement | null;
};

type PayloadStep = {
  id: number;
  text?: string;
  stepTypeId?: number;
  characterId?: number | null;
  speakerMemoryId?: number | null;
  characterExpressionId?: number | null;
  characterPlacementSide?: string | null;
  check?: StepCheck;
  switch?: StepCheckSwitch[];
  action?: BranchStepMemoryAction | null;
  roll?: BranchStepMemoryRoll | null;
  answers?: PayloadAnswer[];
};

type PayloadNode = {
  id: number;
  title?: string;
  description?: string;
  gotoBranchId?: number | null;
  locationId?: number | null;
  stepTypeMode?: string;
  steps?: PayloadStep[];
};

export type EditContextPayload = {
  nodes: PayloadNode[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Shallow deep-equal via JSON serialization.
 * Sufficient for plain data objects (check, switch) that contain no functions or cycles.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Resolve speaker alias (case-insensitive) to characterId.
 * Returns undefined if alias is empty or not found (leave characterId unchanged).
 */
function resolveCharacterId(
  speakerAlias: string,
  characters: Character[],
): number | null | undefined {
  const cleaned = EpisodeTextImportHelper.removeAllCommandsFromString(speakerAlias).trim();
  if (!cleaned) return null; // speaker cleared → null (e.g. Dialogue → Narration)
  const match = characters.find(
    (c) => c.alias.toLowerCase() === cleaned.toLowerCase(),
  );
  return match ? match.id : undefined; // undefined = unknown alias, skip field
}

/**
 * Resolve #goto command from import step commands to a branchId.
 * Uses originalBranches as the lookup table (name → id).
 */
function resolveGotoBranchId(
  importStep: EpisodeTextImportStep,
  originalBranches: Branch[],
): number | null | undefined {
  const gotoCommand = importStep.commands.get(EpisodeTextImportCommandKey.Goto);
  if (!gotoCommand || gotoCommand.size === 0) return undefined; // no goto command = skip field
  const gotoNodeName = gotoCommand.values().next().value?.[2];
  if (!gotoNodeName) return null; // malformed goto → clear
  const target = originalBranches.find(
    (b) => b.title?.toLowerCase() === gotoNodeName.toLowerCase(),
  );
  return target ? target.id : undefined; // unknown target → skip
}

/**
 * Find the original BranchStep by realStepId in the original Branch.
 */
function findOriginalStep(branch: Branch, stepId: number): BranchStep | undefined {
  return branch.steps?.find((s) => s.id === stepId);
}

/**
 * Find the original answer by realAnswerId across all steps in the original Branch.
 */
function findOriginalAnswer(branch: Branch, answerId: number) {
  for (const step of branch.steps ?? []) {
    const answer = step.answers?.find((a) => a.id === answerId);
    if (answer) return { answer, step };
  }
  return undefined;
}

function normalizeActionForPayload(
  action: BranchStepMemoryAction,
  memoryBank: MemoryDTO[],
  characters: Character[],
): BranchStepMemoryAction {
  const characterMemory = memoryBank.find(
    (memory) => Number(memory.id) === Number(action.variableId) && isCharacterReferenceMemoryType(memory.type),
  );
  if (!characterMemory || action.type !== 'set') {
    return action;
  }

  const { value } = action;
  if (value && typeof value === 'object' && 'type' in value && value.type === MemoryCheckValueType.Variable) {
    return action;
  }

  if (typeof value !== 'string') {
    return action;
  }

  const matchedCharacter = characters.find(
    (character) => character.alias?.toLowerCase() === value.toLowerCase(),
  );
  if (matchedCharacter) {
    return {
      ...action,
      value: Number(matchedCharacter.id),
    } as BranchStepMemoryAction;
  }

  return action;
}

/**
 * Build a PayloadStep from an EpisodeTextImportStep that has a realStepId,
 * diffing against the original BranchStep.
 */
function normalizeCheckForPayload(
  check: StepCheck,
  memoryBank: MemoryDTO[],
  characters: Character[],
): StepCheck {
  const targetMemory = memoryBank.find((m) => Number(m.id) === Number(check.variableId));
  if (!targetMemory || !isCharacterReferenceMemoryType(targetMemory.type)) {
    return check;
  }
  const { value } = check;
  if (value && typeof value === 'object' && 'type' in value && value.type === MemoryCheckValueType.Variable) {
    return check;
  }
  if (typeof value !== 'string') {
    return check;
  }
  const matchedCharacter = characters.find(
    (c) => c.alias?.toLowerCase() === value.toLowerCase(),
  );
  if (!matchedCharacter) {
    return check;
  }
  return { ...check, value: String(matchedCharacter.id) };
}

function buildStepDiff(
  importStep: EpisodeTextImportStep,
  originalStep: BranchStep,
  characters: Character[],
  possibleExpressions: ExpressionConfig[],
  memoryBank: MemoryDTO[],
): PayloadStep | null {
  const diff: PayloadStep = { id: importStep.realStepId! };
  let hasChanges = false;

  // text (speakerText → step.text)
  const currentText = EpisodeTextImportHelper.removeAllCommandsFromString(importStep.speakerText).trim();
  if (currentText !== (originalStep.text ?? '').trim()) {
    diff.text = currentText;
    hasChanges = true;
  }

  // stepTypeId (may change via reparse, e.g. Narration → Dialogue)
  const currentStepTypeId = EpisodeTextImportHelper.getStepTypeIdByEntityType(importStep.type);
  if (currentStepTypeId !== undefined && currentStepTypeId !== originalStep.stepTypeId) {
    diff.stepTypeId = currentStepTypeId;
    hasChanges = true;
  }

  // speaker: either CharacterMemory (memory.-prefixed) or static CharacterSkin alias.
  // When CharacterMemory is used we ALWAYS emit speakerMemoryId + characterId:null in the payload
  // so the backend cannot silently drop the memory reference and fall back to a stale characterId.
  if (importStep.speakerMemoryId != null) {
    diff.speakerMemoryId = importStep.speakerMemoryId;
    diff.characterId = null;
    hasChanges = true;
  } else {
    const resolvedCharId = resolveCharacterId(importStep.speakerName, characters);
    if (resolvedCharId !== undefined) {
      const origCharId = originalStep.characterId ?? null;
      if (resolvedCharId !== origCharId) {
        diff.characterId = resolvedCharId;
        hasChanges = true;
      }
    }
    const origMemoryId = (originalStep as { speakerMemoryId?: number | null }).speakerMemoryId ?? null;
    if (origMemoryId !== null) {
      diff.speakerMemoryId = null;
      hasChanges = true;
    }
  }

  // characterExpressionId (description commands → expression id)
  const resolvedExprId = resolveExpressionId(importStep.commands, possibleExpressions);
  const origExprId = originalStep.characterExpressionId ?? null;
  if (resolvedExprId !== undefined) {
    // Known expression found in commands
    if (resolvedExprId !== origExprId) {
      diff.characterExpressionId = resolvedExprId;
      hasChanges = true;
    }
  } else if (origExprId != null) {
    // No expression command found — if original had one, clear it
    diff.characterExpressionId = null;
    hasChanges = true;
  }

  // check & switch (AD_FIELDS — stored in additionalData on backend)
  if (importStep.check !== undefined) {
    const normalizedImportCheck = normalizeCheckForPayload(importStep.check, memoryBank, characters);
    const normalizedOriginalCheck = originalStep.check
      ? normalizeCheckForPayload(originalStep.check, memoryBank, characters)
      : originalStep.check;
    if (!deepEqual(normalizedImportCheck, normalizedOriginalCheck)) {
      diff.check = normalizedImportCheck;
      hasChanges = true;
    }
  }
  if (importStep.switch !== undefined) {
    if (!deepEqual(importStep.switch, originalStep.switch)) {
      diff.switch = importStep.switch;
      hasChanges = true;
    }
  }

  // action (AD_FIELD — #REMEMBER step)
  if (importStep.action !== undefined) {
    const normalizedImportAction = normalizeActionForPayload(importStep.action, memoryBank, characters);
    const normalizedOriginalAction = originalStep.action
      ? normalizeActionForPayload(originalStep.action, memoryBank, characters)
      : originalStep.action;
    if (!deepEqual(normalizedImportAction, normalizedOriginalAction)) {
      diff.action = normalizedImportAction;
      hasChanges = true;
    }
  }

  // roll (AD_FIELD — #RANDOM step)
  if (importStep.roll !== undefined) {
    if (!deepEqual(importStep.roll, originalStep.roll)) {
      diff.roll = importStep.roll;
      hasChanges = true;
    }
  }

  return hasChanges ? diff : null;
}

/**
 * Reconstruct an AnswerRequirement from the #REQ command on an import step.
 * Returns undefined when no #REQ command is present or the command is invalid.
 *
 * Logic mirrors useImportTextNodeManage.tsx:239-289 — kept in sync deliberately.
 */
function buildRequirementFromCommands(
  importStep: EpisodeTextImportStep,
  memoryBank: MemoryDTO[],
  characters: Character[],
): AnswerRequirement | undefined {
  const reqCommand = importStep.commands.get(EpisodeTextImportCommandKey.StepReq);
  if (!reqCommand || reqCommand.size === 0) return undefined;

  const reqValues = reqCommand.values().next().value;
  const remainder = reqValues?.[2]?.trim() ?? '';
  if (!remainder) return undefined;

  const { reqMemoryName, reqOperatorSymbol, reqValue, reqOtherwise, reqHideIndicator } = parseReqCommand(remainder);
  if (!reqMemoryName || !reqOperatorSymbol) return undefined;

  const reqMemory = memoryBank.find(
    (m) => m.name.toLocaleLowerCase().trim() === reqMemoryName,
  );
  if (!reqMemory) return undefined;

  const operators: StepCheckOperatorType[] = [
    StepCheckOperatorType.Equal,
    StepCheckOperatorType.NotEqual,
    StepCheckOperatorType.Greater,
    StepCheckOperatorType.AtLeast,
    StepCheckOperatorType.Less,
    StepCheckOperatorType.AtMost,
  ];
  const reqOperator = operators.find((op) => CheckOperator[op].short === reqOperatorSymbol);
  if (!reqOperator) return undefined;

  // Determine if value is a variable reference
  const compareMemory = memoryBank.find(
    (m) => m.name.toLocaleLowerCase().trim() === reqValue.toLocaleLowerCase().trim(),
  );

  let checkValue: string | MemorySingleLink;
  if (compareMemory) {
    checkValue = {
      type: MemoryCheckValueType.Variable,
      variableId: compareMemory.id.toString(),
    };
  } else if (reqMemory.type === MemoryType.Boolean) {
    checkValue = String(reqValue.toLocaleLowerCase() === 'yes');
  } else if (reqMemory.type === MemoryType.Character) {
    const matchedCharacter = characters.find(
      (c) => c.alias?.toLowerCase() === reqValue.toLowerCase(),
    );
    checkValue = matchedCharacter ? String(matchedCharacter.id) : reqValue;
  } else {
    checkValue = reqValue;
  }

  return {
    check: {
      variableId: reqMemory.id,
      operator: reqOperator,
      value: checkValue,
    },
    visualTreatmentIfNotMet: reqOtherwise.toLowerCase() === 'disable' ? 'Disable' : 'Hide',
    isBadgeHidden: !reqHideIndicator || /^hide/i.test(reqHideIndicator),
  };
}

/**
 * Build a PayloadAnswer from an EpisodeTextImportStep that has a realAnswerId
 * (Answer rows in the TSV map to BranchStep answers).
 * Diffs text, gotoBranchId, answerTypeId, answerPrice, and requirement.
 */
function buildAnswerStepDiff(
  importStep: EpisodeTextImportStep,
  originalAnswer: AnswerStep,
  originalBranches: Branch[],
  memoryBank: MemoryDTO[],
  characters: Character[],
): PayloadAnswer | null {
  const diff: PayloadAnswer = { id: importStep.realAnswerId! };
  let hasChanges = false;

  // text
  const currentText = EpisodeTextImportHelper.removeAllCommandsFromString(importStep.speakerText).trim();
  if (currentText !== (originalAnswer.text ?? '').trim()) {
    diff.text = currentText;
    hasChanges = true;
  }

  // gotoBranchId (from #goto command in answer description)
  const resolvedGoto = resolveGotoBranchId(importStep, originalBranches);
  if (resolvedGoto !== undefined) {
    const origGoto = originalAnswer.gotoBranchId ?? null;
    if (resolvedGoto !== origGoto) {
      diff.gotoBranchId = resolvedGoto;
      hasChanges = true;
    }
  }

  // answerTypeId + answerPrice (from #premium command)
  const isPremium = importStep.commands.has(EpisodeTextImportCommandKey.PremiumAnswer);
  const origIsPremium = originalAnswer.answerTypeId === AnswerTypeId.Paid;

  if (isPremium && !origIsPremium) {
    // Premium added
    diff.answerTypeId = AnswerTypeId.Paid;
    const premiumCommand = importStep.commands.get(EpisodeTextImportCommandKey.PremiumAnswer);
    const price = Number(premiumCommand?.values().next().value?.[2] ?? 0);
    diff.answerPrice = price;
    hasChanges = true;
  } else if (!isPremium && origIsPremium) {
    // Premium removed → revert to Free
    diff.answerTypeId = AnswerTypeId.Free;
    diff.answerPrice = 0;
    hasChanges = true;
  } else if (isPremium && origIsPremium) {
    // Both premium — check if price changed
    const premiumCommand = importStep.commands.get(EpisodeTextImportCommandKey.PremiumAnswer);
    const price = Number(premiumCommand?.values().next().value?.[2] ?? 0);
    if (price !== (originalAnswer.answerPrice ?? 0)) {
      diff.answerPrice = price;
      hasChanges = true;
    }
  }

  // requirement (from #REQ command)
  const currentReq = buildRequirementFromCommands(importStep, memoryBank, characters);
  const hasReqCommand = importStep.commands.has(EpisodeTextImportCommandKey.StepReq);
  const origReq = originalAnswer.requirement ?? null;

  if (hasReqCommand && currentReq) {
    // #REQ present and valid — diff against original
    if (!deepEqual(currentReq, origReq)) {
      diff.requirement = currentReq;
      hasChanges = true;
    }
  } else if (!hasReqCommand && origReq) {
    // #REQ removed → clear requirement
    diff.requirement = null;
    hasChanges = true;
  }
  // If hasReqCommand but !currentReq → invalid #REQ (e.g. bad memory name) — skip, don't corrupt

  return hasChanges ? diff : null;
}

// ---------------------------------------------------------------------------
// Main builder
// ---------------------------------------------------------------------------

/**
 * Builds a PATCH /edit-context payload from dirty nodes.
 *
 * Compares current EpisodeTextImport state against original Branch[] data
 * and produces a minimal diff containing only changed fields.
 *
 * @param episodeTextImport - Current edited state
 * @param originalBranches - Original Branch[] from the API (preloadedNodes)
 * @param dirtyNodeIds - IDs of nodes marked as dirty (e.g. ["bid:123", "bid:456"])
 * @param characters - Character[] for alias → id resolution
 * @param possibleExpressions - ExpressionConfig[] for expression command → id resolution
 * @param memoryBank - MemoryDTO[] for #REQ requirement resolution (memory name → id)
 */
export function buildEditContextPayload(
  episodeTextImport: EpisodeTextImport,
  originalBranches: Branch[],
  dirtyNodeIds: string[],
  characters: Character[],
  possibleExpressions: ExpressionConfig[],
  memoryBank: MemoryDTO[] = [],
): EditContextPayload {
  const dirtySet = new Set(dirtyNodeIds);
  const branchById = new Map(originalBranches.map((b) => [b.id, b]));
  const payloadNodes: PayloadNode[] = [];

  for (const importNode of episodeTextImport.nodes) {
    if (!dirtySet.has(importNode.id)) continue;
    if (importNode.realBranchId == null) {
      console.warn('[buildEditContextPayload] Dirty node without realBranchId:', importNode.id);
      continue;
    }

    const originalBranch = branchById.get(importNode.realBranchId);
    if (!originalBranch) {
      console.warn('[buildEditContextPayload] Original branch not found for id:', importNode.realBranchId);
      continue;
    }

    const node: PayloadNode = { id: importNode.realBranchId };
    let hasNodeChanges = false;

    // Node-level fields: title, description
    const currentTitle = EpisodeTextImportHelper.removeAllCommandsFromString(importNode.title).trim();
    if (currentTitle !== (originalBranch.title ?? '').trim()) {
      node.title = currentTitle;
      hasNodeChanges = true;
    }

    const currentDesc = EpisodeTextImportHelper.removeAllCommandsFromString(importNode.description).trim();
    if (currentDesc !== (originalBranch.description ?? '').trim()) {
      node.description = currentDesc;
      hasNodeChanges = true;
    }

    // Step-level diffs
    const stepDiffs: PayloadStep[] = [];

    // Collect answer diffs grouped by their parent step
    // Key: original stepId, Value: PayloadAnswer[]
    const answerDiffsByStepId = new Map<number, PayloadAnswer[]>();

    for (const importStep of importNode.steps) {
      // Regular step with realStepId
      if (importStep.realStepId != null) {
        const origStep = findOriginalStep(originalBranch, importStep.realStepId);
        if (origStep) {
          const diff = buildStepDiff(importStep, origStep, characters, possibleExpressions, memoryBank);
          if (diff) stepDiffs.push(diff);
        }
      }

      // Answer step with realAnswerId
      if (importStep.realAnswerId != null) {
        const found = findOriginalAnswer(originalBranch, importStep.realAnswerId);
        if (found) {
          const answerDiff = buildAnswerStepDiff(importStep, found.answer, originalBranches, memoryBank, characters);
          if (answerDiff) {
            const parentStepId = found.step.id;
            if (!answerDiffsByStepId.has(parentStepId)) {
              answerDiffsByStepId.set(parentStepId, []);
            }
            answerDiffsByStepId.get(parentStepId)!.push(answerDiff);
          }
        }
      }

      // Recurse into conditionListItems
      for (const cond of importStep.conditionListItems) {
        if (cond.realStepId != null) {
          const origStep = findOriginalStep(originalBranch, cond.realStepId);
          if (origStep) {
            const diff = buildStepDiff(cond, origStep, characters, possibleExpressions, memoryBank);
            if (diff) stepDiffs.push(diff);
          }
        }
        if (cond.realAnswerId != null) {
          const found = findOriginalAnswer(originalBranch, cond.realAnswerId);
          if (found) {
            const answerDiff = buildAnswerStepDiff(cond, found.answer, originalBranches, memoryBank, characters);
            if (answerDiff) {
              const parentStepId = found.step.id;
              if (!answerDiffsByStepId.has(parentStepId)) {
                answerDiffsByStepId.set(parentStepId, []);
              }
              answerDiffsByStepId.get(parentStepId)!.push(answerDiff);
            }
          }
        }
      }
    }

    // Merge answer diffs into step diffs
    for (const [stepId, answers] of answerDiffsByStepId) {
      const existing = stepDiffs.find((s) => s.id === stepId);
      if (existing) {
        existing.answers = answers;
      } else {
        // Step itself didn't change, but its answers did
        stepDiffs.push({ id: stepId, answers });
      }
    }

    if (stepDiffs.length > 0) {
      node.steps = stepDiffs;
      hasNodeChanges = true;
    }

    if (hasNodeChanges) {
      payloadNodes.push(node);
    }
  }

  return { nodes: payloadNodes };
}
